import {
  action,
  computed,
  makeObservable,
  onBecomeObserved,
  reaction,
} from "mobx"
import axios from "axios"

import {
  cardMassPayoutsApi,
  catchErrors,
  massPayoutsApi,
} from "@src/shared/api"
import {
  CardMassPayoutJsonCreate,
  CardMassPayoutJsonCreatePayouts,
  CardMassPayoutResponse,
  CardMassPayoutShort,
  SymbolCard,
} from "@src/shared/api/generated"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { CollectionFilters } from "@src/shared/componemts/TableFilter/classes/CollectionFilters"
import { filtersPresets } from "@src/shared/componemts/TableFilter/classes/constants"
import { API_PATH } from "@src/shared/config"

export type MassPayoutsCollectionLoadRequest = {
  date?: {
    dateFrom?: string
    dateTo?: string
  }
  externalId?: string
  order?: "asc" | "desc"
  page?: number
}

export type MassPayoutsCollectionFilter = {
  date?: {
    dateFrom?: string
    dateTo?: string
  }
  externalId?: string
  order?: "asc" | "desc"
}

export type FormPayout = {
  amount: string
  card_number: string
  external_id?: string
  symbol: SymbolCard
}

export type CardsPayoutFormValue = {
  payouts: FormPayout[]
}

export class MassPayoutsCardCollection extends loading(
  ["fetch", "_create"],
  CollectionWithPagination<CardMassPayoutShort>,
) {
  constructor() {
    super()

    makeObservable(this, {
      fetch: action.bound,
      filter: computed,
    })

    onBecomeObserved(this, "isLoading", this.load)

    reaction(
      () => this.filter.state,
      (v) => v && this.load(v),
    )
  }

  async _create(
    request: CardMassPayoutJsonCreate | FormData,
  ): Promise<CardMassPayoutResponse | null> {
    try {
      const { data: { data } = {} } = await axios.post(
        `${API_PATH}/v2.0/card-mass-payouts`,
        request,
      )

      return data
    } catch (e) {
      catchErrors(e as any)
    }

    return null
  }

  async createFromCsv(file: Blob): Promise<CardMassPayoutResponse | null> {
    const request = new FormData()

    request.append("payouts", file)
    request.append("id", crypto.randomUUID())
    request.append("with_confirmation", "true")

    return this._create(request)
  }

  async createManual({ payouts }: CardsPayoutFormValue) {
    const request: CardMassPayoutJsonCreate = {
      id: crypto.randomUUID(),
      payouts: payouts.map(
        (p) =>
          ({
            amount: p.amount,
            card_number: p.card_number,
            symbol: p.symbol,
          }) as CardMassPayoutJsonCreatePayouts,
      ),
      with_confirmation: true,
    }

    return this._create(request)
  }

  async fetch(req: MassPayoutsCollectionLoadRequest) {
    try {
      const { data: { data } = {} } =
        await cardMassPayoutsApi.listCardMassPayouts(
          req.externalId,
          req.date?.dateFrom,
          req.date?.dateTo,
          req.order,
          this.calcOffset(req.page),
          this.pageSize,
        )

      if (data) {
        return {
          data: data.data!,
          pagination: data.pagination!,
        }
      }
    } catch (error: any) {
      catchErrors(error)
    }

    return null
  }

  get filter() {
    return new CollectionFilters<MassPayoutsCollectionFilter>([
      CollectionFilters.createField("externalId").search({
        col: 6,
        label: "Search for External ID",
        placeholder: "External ID",
      }),
      CollectionFilters.createField("date").date({ col: 8, label: "Date" }),
      {
        ...filtersPresets.order,
        col: 4,
        label: "Select on order",
        placeholder: "select",
      },
    ])
  }
}
